import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, FilePlus } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { NumberInput } from '@/components/ui/NumberInput'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { SubscribersService, type Subscriber } from '@/services/modules/SubscribersService'
import { RevenuesService, type Revenue } from '@/services/modules/RevenuesService'
import { useRevenueActions } from '@/pages/Revenues/useRevenueActions'
import { useRevenueMaintenance } from '@/pages/Revenues/useRevenueMaintenance'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { AsaasBadge } from '@/components/financial/AsaasBadge'
import { useAuthority } from '@/hooks/useAuthority'

/** Bolinha de status da fatura (mesma paleta de /revenues). */
const STATUS_DOT: Record<string, { color: string; label: string }> = {
  PENDENTE:    { color: 'bg-amber-400',   label: 'Pendente' },
  PAGO:        { color: 'bg-emerald-500', label: 'Pago' },
  PROCESSADO:  { color: 'bg-blue-500',    label: 'Processado' },
  ISENTO:      { color: 'bg-blue-500',    label: 'Isento' },
  CANCELADO:   { color: 'bg-rose-500',    label: 'Cancelado' },
  RECUSADO:    { color: 'bg-rose-500',    label: 'Recusado' },
  REEMBOLSADO: { color: 'bg-blue-500',    label: 'Reembolsado' },
}

function RevenueStatusDot({ status }: { status: string }) {
  const cfg = STATUS_DOT[status] ?? { color: 'bg-gray-300', label: status }
  return (
    <span className="inline-flex items-center justify-center" title={cfg.label}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${cfg.color}`} />
    </span>
  )
}

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
].map((m, i) => ({ value: String(i + 1), label: m }))

export function SubscriberInvoicesPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [subscriber, setSubscriber] = useState<Subscriber | null>(null)
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canGenerate = can('commercial.subscribers.invoices')

  function refresh() { setReload(v => v + 1) }
  const { rowActions, dialogs } = useRevenueActions(refresh)
  // Manutenção financeira (busca no Asaas, datas, exclusão) — só aparece para
  // quem tem as permissões; o hook devolve vazio para os demais.
  const maintenance = useRevenueMaintenance({ subscriberId: id }, refresh)

  const filters = useMemo(() => ({ subscriber_id: id }), [id])
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(RevenuesService, { size: 25, reloadKey: reload, filters })

  useEffect(() => {
    if (!id) return
    SubscribersService.show(id).then(setSubscriber).catch(() => {
      toast.error({ title: 'Erro', description: 'Não foi possível carregar o assinante.' })
    })
  }, [id])

  const exp = useDataTableExport<Revenue>({
    filename: 'faturas-assinante',
    service: RevenuesService,
    filters,
    columns: [
      { header: 'Código',     accessor: (r: Revenue) => r.code },
      { header: 'Vencimento', accessor: (r: Revenue) => r.formatted?.dueDate ?? '' },
      { header: 'Pagamento',  accessor: (r: Revenue) => r.formatted?.paidDate ?? '' },
      { header: 'Valor',      accessor: (r: Revenue) => r.formatted?.amount ?? String(r.amount) },
      { header: 'Forma de pagamento', accessor: (r: Revenue) => r.method ?? '' },
      { header: 'Origem',     accessor: (r: Revenue) => r.hasAsaasCharge === true ? 'Asaas' : r.hasAsaasCharge === false ? 'Sistema' : 'Não verificado' },
      { header: 'Status',     accessor: (r: Revenue) => r.status },
    ],
  })

  const columns: DataTableColumn<Revenue>[] = [
    { key: 'due',      header: 'Vencimento', alwaysVisible: true, align: 'center', className: 'whitespace-nowrap', render: r => r.formatted?.dueDate || '—' },
    { key: 'paid',     header: 'Pagamento', align: 'center', className: 'whitespace-nowrap', render: r => r.formatted?.paidDate || '—' },
    { key: 'subtotal', header: 'Subtotal', align: 'center', className: 'whitespace-nowrap', render: r => r.formatted?.subtotal || '—' },
    { key: 'discount', header: 'Descontos', align: 'center', className: 'whitespace-nowrap', render: r => r.formatted?.discount || '—' },
    { key: 'total',    header: 'Total', align: 'center', className: 'whitespace-nowrap font-medium', render: r => r.formatted?.amount || '—' },
    { key: 'method',   header: 'Forma de pagamento', align: 'center', render: r => <span className="text-xs text-gray-600">{r.method || '—'}</span> },
    { key: 'origin',   header: 'Origem', align: 'center', className: 'w-28', render: r => <AsaasBadge revenue={r} /> },
    { key: 'status',   header: 'Status', align: 'center', className: 'w-24', render: r => <RevenueStatusDot status={r.status} /> },
  ]

  return (
    <>
      <PageHeader
        title={`Faturas — ${subscriber?.name ?? ''}`}
        subtitle="Faturas do assinante individual"
        crumbs={[{ label: 'Comercial' }, { label: 'Assinantes' }, { label: 'Faturas' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" iconLeft={<ArrowLeft size={14} />} onClick={() => navigate('/commercial/subscribers')}>Voltar</Button>
            {maintenance.toolbarAction}
            {canGenerate && <GenerateInvoiceButton subscriberId={id} onGenerated={refresh} />}
          </div>
        }
      />
      <DataTable
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(r) => [...rowActions(r), ...maintenance.rowActions(r)]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      {dialogs}
      {maintenance.dialogs}
    </>
  )
}

// ---------------------------------------------------------------------------
// Botão + modal "Gerar fatura" — escolhe mês/ano e gera 1 fatura pelo plano.
// ---------------------------------------------------------------------------
function GenerateInvoiceButton({ subscriberId, disabled, onGenerated }: {
  subscriberId: string
  disabled?: boolean
  onGenerated: () => void
}) {
  const now = new Date()
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(String(now.getMonth() + 1))
  const [year, setYear] = useState<number | null>(now.getFullYear())
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!year) {
      toast.error({ title: 'Ano obrigatório', description: 'Informe o ano.' })
      return
    }
    setSaving(true)
    try {
      const res = await SubscribersService.generateInvoicesRange(subscriberId, {
        start_month: Number(month), start_year: year,
        end_month: Number(month), end_year: year,
      })
      if (res.failed && res.failed > 0) {
        toast.error({ title: 'Cobrança não criada no Asaas', description: 'A fatura NÃO foi gerada. Veja Integrações → Logs de erro.' })
      } else if (res.created > 0) {
        toast.success({ title: 'Fatura gerada', description: 'A fatura do período foi criada com base no plano.' })
      } else {
        toast.warning({ title: 'Já existe', description: 'Já há uma fatura para este mês/ano.' })
      }
      setOpen(false)
      onGenerated()
    } catch (e) {
      toast.error({ title: 'Não foi possível gerar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <>
      <Button iconLeft={<FilePlus size={14} />} disabled={disabled} onClick={() => setOpen(true)}>Gerar fatura</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Gerar fatura"
        size="sm"
        footer={<>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} loading={saving}>Gerar</Button>
        </>}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">A fatura será gerada com base no plano do assinante. Não é permitido duplicar a competência (mês/ano).</p>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Mês" value={month} onChange={(e) => setMonth(e.target.value)} options={MONTHS} searchable={false} clearable={false} />
            <NumberInput label="Ano" value={year} onChange={setYear} decimals={0} min={2020} max={2100} />
          </div>
        </div>
      </Modal>
    </>
  )
}
